import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User,
  UserCredential,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { getDoc, doc, setDoc, serverTimestamp, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { UserProfile, rolePermissions } from '../types/UserProfile';
import { db } from '../lib/firebase';
import { toast } from "sonner";
import ErrorLoggingService from '../services/errorLoggingService';
import PasswordService from '../services/passwordService';
import PermissionService from '../services/permissionService';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signup: (email: string, password: string, profile: Omit<UserProfile, 'createdAt' | 'updatedAt'>) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (displayName: string) => Promise<void>;
  userProfile: UserProfile | null;
  resendVerificationEmail: () => Promise<void>;
  isEmailVerified: boolean;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [previousUser, setPreviousUser] = useState<User | null>(null);

  async function signup(email: string, password: string, profile: Omit<UserProfile, 'createdAt' | 'updatedAt'>) {
    try {
      // Create the user with Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user profile in Firestore with default role and permissions
      const newProfile: UserProfile = {
        ...profile,
        id: user.uid,
        email: email,
        role: 'user',
        permissions: rolePermissions['user'],
        createdAt: new Date(),
        updatedAt: new Date(),
        emailVerified: true, // Auto-verify email
        failedLoginAttempts: 0,
        accountLocked: false,
        twoFactorEnabled: false
      };

      await setDoc(doc(db, 'users', user.uid), {
        ...newProfile,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return userCredential;
    } catch (error) {
      throw error;
    }
  }

  async function login(email: string, password: string) {
    try {
      // Simple authentication without Firestore checks
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Create a basic user profile if it doesn't exist
      const user = userCredential.user;
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        const newProfile: UserProfile = {
          id: user.uid,
          email: email,
          firstName: email.split('@')[0], // Use email username as first name
          lastName: '', // Empty last name
          role: 'user',
          permissions: rolePermissions['user'],
          createdAt: new Date(),
          updatedAt: new Date(),
          emailVerified: true,
          failedLoginAttempts: 0,
          accountLocked: false,
          twoFactorEnabled: false
        };

        await setDoc(doc(db, 'users', user.uid), {
          ...newProfile,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      return userCredential;
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email format.');
      } else {
        throw new Error('Failed to sign in. Please try again.');
      }
    }
  }

  function logout() {
    return signOut(auth);
  }

  function resetPassword(email: string) {
    return sendPasswordResetEmail(auth, email);
  }

  function updateUserProfile(displayName: string) {
    if (!currentUser) throw new Error('No user logged in');
    return updateProfile(currentUser, { displayName });
  }

  async function resendVerificationEmail() {
    if (!currentUser) throw new Error('No user logged in');
    await sendEmailVerification(currentUser);
    toast.success("Verification email sent successfully!");
  }

  function hasPermission(permission: string): boolean {
    return PermissionService.hasPermission(userProfile, permission as any);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !previousUser) {
        if ('speechSynthesis' in window) {
          // Get available voices
          const voices = window.speechSynthesis.getVoices();
          
          // Try to find a modern, tech-sounding voice
          // Prefer voices like Google UK English Female, Microsoft David, or similar
          const preferredVoice = voices.find(voice => 
            voice.name.includes('Google') || 
            voice.name.includes('Microsoft') ||
            voice.name.includes('Samantha')
          ) || voices[0];

          const message = new SpeechSynthesisUtterance('Welcome to Truth Nexus');
          
          // Configure voice settings
          message.voice = preferredVoice;
          message.pitch = 0.9; // Slightly lower pitch for a more professional sound
          message.rate = 0.9; // Slightly slower rate for clarity
          message.volume = 1.0; // Full volume
          
          // Add a small pause before speaking
          setTimeout(() => {
            window.speechSynthesis.speak(message);
          }, 500);
        }
      }

      setPreviousUser(user);

      if (user) {
        try {
          // Always reload the user to get the latest emailVerified status
          await user.reload();
          
          // Update the current user after reload to ensure we have the latest data
          const refreshedUser = auth.currentUser;
          setCurrentUser(refreshedUser);
          
          // Check and set verification status
          const verified = refreshedUser?.emailVerified ?? false;
          setIsEmailVerified(verified);
          
          // Fetch user profile if email is verified
          if (verified) {
            try {
              const userDoc = await getDoc(doc(db, 'users', user.uid));
              if (userDoc.exists()) {
                const profile = userDoc.data() as UserProfile;
                
                // Validate user permissions
                if (!PermissionService.validateUserPermissions(profile)) {
                  ErrorLoggingService.logError(
                    'Invalid user permissions detected',
                    'high',
                    user.uid,
                    'permission_validation'
                  );
                }
                
                setUserProfile(profile);
                
                // Update the emailVerified field in Firestore if it's now verified
                if (verified && profile.emailVerified === false) {
                  await setDoc(doc(db, 'users', user.uid), 
                    { emailVerified: true, updatedAt: serverTimestamp() }, 
                    { merge: true }
                  );
                }
              }
            } catch (error) {
              ErrorLoggingService.logError(
                error,
                'high',
                user.uid,
                'profile_fetch_error'
              );
            }
          } else {
            setUserProfile(null);
          }
        } catch (error) {
          ErrorLoggingService.logError(
            error,
            'high',
            user.uid,
            'user_reload_error'
          );
          setCurrentUser(user);
          setIsEmailVerified(user.emailVerified);
          setUserProfile(null);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setIsEmailVerified(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [previousUser]);

  const value = {
    currentUser,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    userProfile,
    resendVerificationEmail,
    isEmailVerified,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
